import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText } from "lucide-react";

const LegalPage = () => {
  const { section } = useParams();
  const navigate = useNavigate();

  // Contenu complet des conditions d'utilisation
  const legalContent = `
    <h1>Conditions d'utilisation, Politique de confidentialité et Politique de vie privée</h1>
    <p><strong>Dernière mise à jour :</strong> Janvier 2025</p>
    
    <p>Ce document constitue une entente légale entre l'utilisateur et <strong>SNE - Solutions Numérique en Enquêtes</strong> concernant l'utilisation de l'API.</p>
    
    <h2>1. Description du service</h2>
    <p>L'API permet d'effectuer des recherches vers des bases de données, registres, sites web et services tiers afin d'obtenir des résultats sous forme de liens, références ou métadonnées. Le Fournisseur n'héberge aucun contenu et n'en contrôle aucune source.</p>
    
    <h2>2. Abonnements et paiements</h2>
    <p>La facturation est assurée par <strong>Shopify</strong>. Toutes les conditions de paiement, taxes, renouvellements et politiques de facturation relèvent exclusivement de Shopify et peuvent être consultées sur leur site officiel.</p>
    <p>Tous les paiements sont finaux et non remboursables. Aucune annulation n'est possible en cours de période. Les plans mensuels se renouvellent automatiquement et l'utilisateur est responsable de mettre fin à son abonnement.</p>
    
    <h2>3. Disponibilité</h2>
    <p>L'API est fournie telle quelle et selon la disponibilité. Aucune garantie de continuité, de performance ou d'exactitude n'est offerte.</p>
    
    <h2>4. Sources externes</h2>
    <p>Les résultats proviennent de sources externes indépendantes. Le Fournisseur ne garantit ni leur exactitude, ni leur légalité, ni leur disponibilité.</p>
    
    <h2>5. Limitation de responsabilité</h2>
    <p>Le Fournisseur ne pourra être tenu responsable des pertes financières, pertes de données ou dommages indirects résultant de l'utilisation de l'API.</p>
    
    <h2>6. Confidentialité</h2>
    <p>L'API traite uniquement les données nécessaires à son fonctionnement : clés API, paramètres de requêtes, adresses IP et identifiants de compte. Aucun résultat n'est stocké. Aucune donnée n'est vendue.</p>
    
    <h2>7. Vie privée</h2>
    <p>Nous collectons les renseignements nécessaires à la gestion des comptes et des abonnements. Toutes les informations de paiement sont traitées exclusivement par Shopify. Le Fournisseur n'a jamais accès aux données de carte bancaire.</p>
    
    <h2>8. Droits</h2>
    <p>Conformément aux lois du Québec et du Canada, l'utilisateur peut demander l'accès, la correction ou la suppression de ses données.</p>
    
    <h2>9. Propriété intellectuelle et protection de l'idée</h2>
    <p>L'ensemble de la plateforme SNE - Solutions Numérique en Enquêtes, incluant mais sans s'y limiter :</p>
    <ul>
      <li>Le concept, l'idée et le modèle d'affaires</li>
      <li>L'architecture logicielle et le code source</li>
      <li>L'interface utilisateur et le design</li>
      <li>Les algorithmes, méthodes et processus de recherche</li>
      <li>La sélection et l'organisation des sources de données</li>
      <li>La documentation, les guides et le matériel de formation</li>
      <li>Les marques de commerce, logos et éléments visuels</li>
    </ul>
    <p>sont la propriété exclusive de SNE - Solutions Numérique en Enquêtes et sont protégés par les lois canadiennes et internationales sur la propriété intellectuelle, le droit d'auteur et les secrets commerciaux.</p>
    
    <h3>9.1 Interdictions</h3>
    <p>Il est strictement interdit, sans autorisation écrite préalable du Fournisseur, de :</p>
    <ul>
      <li><strong>Reproduire</strong> tout ou partie de la plateforme, du code ou des fonctionnalités</li>
      <li><strong>Copier, imiter ou s'inspirer</strong> du concept, de l'idée ou du modèle d'affaires pour créer un produit concurrent ou similaire</li>
      <li><strong>Décompiler, désassembler ou procéder à l'ingénierie inverse</strong> de tout composant de l'API</li>
      <li><strong>Distribuer, vendre, sous-licencier ou transférer</strong> l'accès à la plateforme à des tiers</li>
      <li><strong>Utiliser les informations obtenues</strong> lors de l'utilisation ou de la démonstration pour développer un produit concurrent</li>
      <li><strong>Divulguer à des tiers</strong> les méthodes, processus ou informations techniques observés</li>
    </ul>
    
    <h3>9.2 Sanctions</h3>
    <p>Toute violation des présentes dispositions relatives à la propriété intellectuelle pourra donner lieu à :</p>
    <ul>
      <li>La résiliation immédiate de l'accès sans remboursement</li>
      <li>Des poursuites judiciaires pour violation de droits d'auteur et de propriété intellectuelle</li>
      <li>Des réclamations en dommages-intérêts, incluant les profits perdus et les frais juridiques</li>
      <li>Des injonctions pour faire cesser toute utilisation non autorisée</li>
    </ul>
    
    <h2>10. Responsabilité de l'utilisateur et conformité OSINT</h2>
    <p><strong>L'utilisateur est seul et entièrement responsable</strong> de l'utilisation qu'il fait des données, informations et résultats obtenus via la plateforme SNE.</p>
    
    <h3>10.1 Obligations de l'utilisateur</h3>
    <p>En utilisant la plateforme, l'utilisateur s'engage à :</p>
    <ul>
      <li><strong>Respecter toutes les lois applicables</strong>, notamment les lois sur la protection de la vie privée, la Loi sur la protection des renseignements personnels et les documents électroniques (LPRPDE), la Loi 25 du Québec et toute autre législation pertinente</li>
      <li><strong>Utiliser les données uniquement à des fins légitimes</strong> et conformes à l'éthique professionnelle de son secteur d'activité</li>
      <li><strong>Ne pas utiliser la plateforme</strong> à des fins de harcèlement, de surveillance illégale, d'usurpation d'identité ou de toute autre activité illicite</li>
      <li><strong>Vérifier l'exactitude</strong> des informations obtenues avant de les utiliser pour prendre des décisions importantes</li>
      <li><strong>Obtenir les autorisations nécessaires</strong> lorsque requis par la loi avant de collecter ou d'utiliser des renseignements personnels</li>
      <li><strong>Conserver et traiter les données</strong> conformément aux exigences légales en matière de protection des renseignements personnels</li>
    </ul>
    
    <h3>10.2 Exonération de responsabilité</h3>
    <p>SNE - Solutions Numérique en Enquêtes :</p>
    <ul>
      <li><strong>N'assume aucune responsabilité</strong> quant à l'usage que fait l'utilisateur des données récoltées via l'API</li>
      <li><strong>Ne garantit pas</strong> que l'utilisation des résultats de recherche par l'utilisateur sera conforme aux lois applicables dans sa juridiction</li>
      <li><strong>Se dégage de toute responsabilité</strong> en cas de réclamation, poursuite ou dommage résultant de l'utilisation des données par l'utilisateur</li>
      <li><strong>N'est pas responsable</strong> des décisions prises par l'utilisateur sur la base des informations obtenues via la plateforme</li>
    </ul>
    
    <h3>10.3 Indemnisation</h3>
    <p>L'utilisateur s'engage à <strong>indemniser, défendre et tenir indemne</strong> SNE - Solutions Numérique en Enquêtes, ses dirigeants, employés et partenaires contre toute réclamation, perte, dommage, responsabilité, coût et dépense (incluant les frais juridiques raisonnables) découlant de :</p>
    <ul>
      <li>L'utilisation de la plateforme par l'utilisateur</li>
      <li>La violation des présentes conditions par l'utilisateur</li>
      <li>La violation de droits de tiers, incluant les droits à la vie privée</li>
      <li>Toute activité illégale ou non éthique menée par l'utilisateur via la plateforme</li>
    </ul>
    
    <h2>11. Résiliation</h2>
    <p>Le Fournisseur peut suspendre ou fermer un compte en cas d'abus ou de violation, sans remboursement.</p>
    
    <h2>12. Droit applicable</h2>
    <p>Ces conditions sont régies par les lois de la province de Québec et les lois fédérales du Canada. Tout litige relève des tribunaux du Québec.</p>
    
    <h2>13. Acceptation</h2>
    <p>En utilisant la plateforme SNE, l'utilisateur reconnaît avoir lu, compris et accepté l'ensemble de ces conditions, incluant les dispositions relatives à la propriété intellectuelle, à la protection de l'idée et à sa responsabilité personnelle quant à l'utilisation des données.</p>
  `;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 py-12 pt-24 px-6">
      <div className="max-w-4xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/")}
          className="text-gray-400 hover:text-white mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l&apos;accueil
        </Button>

        <div className="bg-gray-900/50 rounded-2xl border border-gray-800 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="text-amber-500"><FileText className="w-6 h-6" /></div>
            <h1 className="text-2xl font-bold text-white">Mentions légales</h1>
          </div>
          <div 
            className="prose prose-invert prose-amber max-w-none legal-content"
            dangerouslySetInnerHTML={{ __html: legalContent }}
          />
        </div>
      </div>

      <style>{`
        .legal-content h1 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #f59e0b;
          margin-bottom: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #374151;
        }
        .legal-content h2 {
          font-size: 1.125rem;
          font-weight: 600;
          color: #f5f5f5;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .legal-content h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #d1d5db;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
        }
        .legal-content p {
          color: #9ca3af;
          margin-bottom: 1rem;
          line-height: 1.7;
        }
        .legal-content strong {
          color: #f59e0b;
        }
        .legal-content ul {
          color: #9ca3af;
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }
        .legal-content li {
          margin-bottom: 0.5rem;
          list-style-type: disc;
        }
      `}</style>
    </div>
  );
};

export default LegalPage;
